import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Zap } from "lucide-react";
import { isNativeApp, fireAndForget } from "@/lib/safeDespia";
import { toast } from "sonner";
import { logPaywallEvent } from "@/lib/paywallAnalytics";

interface PaywallButtonProps {
  externalId: string;
  offering: string;
  isPending?: boolean;
  variant?: "default" | "glow" | "outline" | "premium";
  size?: "default" | "sm" | "lg" | "xl";
  className?: string;
  children?: React.ReactNode;
  onComplete?: () => void;
  /** What Pro feature triggered this paywall — used for analytics */
  feature?: string;
  /** If false, skip logging the "viewed" event on mount (parent already logged) */
  logViewOnMount?: boolean;
}

export function PaywallButton({
  externalId,
  offering,
  isPending = false,
  variant = "glow",
  size = "default",
  className,
  children,
  onComplete,
  feature,
  logViewOnMount = true,
}: PaywallButtonProps) {
  const viewLogged = useRef(false);
  // Never log "unknown" — fall back to the page the button lives on.
  const resolvedFeature =
    feature ||
    `page${typeof window !== "undefined" ? window.location.pathname.replace(/\//g, "_") : ""}`;
  useEffect(() => {
    if (!logViewOnMount || viewLogged.current) return;
    viewLogged.current = true;
    void logPaywallEvent("viewed", { feature: resolvedFeature, offering });
  }, [logViewOnMount, resolvedFeature, offering]);

  const handleLaunchPaywall = () => {
    void logPaywallEvent("trial_tap", { feature: resolvedFeature, offering });
    if (isNativeApp()) {
      fireAndForget("lighthaptic://");
      fireAndForget(`revenuecat://launchPaywall?external_id=${externalId}&offering=${offering}`);
    } else {
      toast.info("Native paywall available in the mobile app", {
        description: "Download our app to start your free trial.",
      });
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleLaunchPaywall}
      disabled={isPending}
      className={className}
    >
      {isPending ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <Zap className="w-4 h-4 mr-2" />
          {children || "Start Free Trial"}
        </>
      )}
    </Button>
  );
}

